import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, Button, IconButton, Chip, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  LinearProgress, CircularProgress, Collapse, Tabs, Tab,
  Accordion, AccordionSummary, AccordionDetails,
  Tooltip, alpha, useTheme
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import StraightenIcon from '@mui/icons-material/Straighten';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import VideoFileIcon from '@mui/icons-material/VideoFile';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import NavigationIcon from '@mui/icons-material/Navigation';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import api from '../../../services/api';

const DEEP_PURPLE = '#7c4dff';

// Tilt interpretation bands from methodology doc
const TILT_BANDS = [
  { min: 0, max: 0.5, label: 'Essentially Vertical', color: '#4caf50', icon: CheckCircleIcon },
  { min: 0.5, max: 2.0, label: 'Minor Tilt (Monitor)', color: '#8bc34a', icon: InfoIcon },
  { min: 2.0, max: 5.0, label: 'Notable Tilt (Investigate)', color: '#ff9800', icon: WarningAmberIcon },
  { min: 5.0, max: Infinity, label: 'Significant Tilt (Immediate)', color: '#f44336', icon: ErrorIcon },
];

// Processing pipeline stages
const PROCESSING_STAGES = [
  'Extracting frames from orbit video...',
  'Running AI pole segmentation...',
  'Detecting edge points...',
  'Fitting profile lines (RANSAC)...',
  'Computing tilt angles...',
  'Generating assessment report...',
];

// Frame stage labels for detail view
const FRAME_STAGES = [
  { key: 'raw', label: 'Raw Frame' },
  { key: 'segmentation', label: 'Segmentation' },
  { key: 'edge_points', label: 'Edge Points' },
  { key: 'profile_lines', label: 'Profile Lines' },
  { key: 'tilt_result', label: 'Tilt Result' },
  { key: 'geometry', label: 'Geometry' },
];

const getTiltBand = (angle) => {
  const absAngle = Math.abs(angle);
  return TILT_BANDS.find(b => absAngle >= b.min && absAngle < b.max) || TILT_BANDS[TILT_BANDS.length - 1];
};

const getDirectionLabel = (degrees) => {
  const dirs = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
  const index = Math.round(degrees / 45) % 8;
  return dirs[index];
};

const SlantingAnalysisModal = ({ open, onClose, asset }) => {
  const theme = useTheme();
  const fileInputRef = useRef(null);

  // Core state
  const [analysisData, setAnalysisData] = useState(null);
  const [status, setStatus] = useState('idle'); // idle | uploading | processing | completed | error
  const [loading, setLoading] = useState(false);

  // Upload state
  const [selectedFile, setSelectedFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  // Processing simulation state
  const [processingStage, setProcessingStage] = useState(0);
  const [processingProgress, setProcessingProgress] = useState(0);

  // UI state
  const [expandedFrame, setExpandedFrame] = useState(null);
  const [frameStageTab, setFrameStageTab] = useState(0);

  // Fetch existing analysis on open
  useEffect(() => {
    if (!open || !asset?.id) return;
    const fetchAnalysis = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/slanting/analysis/${asset.id}`);
        if (res.data.success && res.data.data) {
          setAnalysisData(res.data.data);
          setStatus('completed');
        }
      } catch (err) {
        // No analysis exists — stay idle
        setAnalysisData(null);
        setStatus('idle');
      } finally {
        setLoading(false);
      }
    };
    fetchAnalysis();
  }, [open, asset?.id]);

  // Reset on close
  useEffect(() => {
    if (!open) {
      setSelectedFile(null);
      setDragOver(false);
      setProcessingStage(0);
      setProcessingProgress(0);
      setExpandedFrame(null);
      setFrameStageTab(0);
    }
  }, [open]);

  // Processing simulation
  const simulateProcessing = useCallback(() => {
    setStatus('processing');
    setProcessingStage(0);
    setProcessingProgress(0);

    const totalStages = PROCESSING_STAGES.length;
    const intervalMs = 500; // 500ms per stage = 3s total
    let currentStage = 0;

    const interval = setInterval(() => {
      currentStage++;
      if (currentStage >= totalStages) {
        clearInterval(interval);
        setProcessingProgress(100);
        // Fetch results (mock)
        api.get(`/slanting/analysis/${asset.id}`)
          .then(res => {
            if (res.data.success && res.data.data) {
              setAnalysisData(res.data.data);
            }
            setStatus('completed');
          })
          .catch(() => setStatus('error'));
      } else {
        setProcessingStage(currentStage);
        setProcessingProgress(Math.round((currentStage / totalStages) * 100));
      }
    }, intervalMs);
  }, [asset?.id]);

  // Handlers
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) setSelectedFile(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file && (file.type.startsWith('video/') || file.name.match(/\.(mp4|mov|avi)$/i))) {
      setSelectedFile(file);
    }
  };

  const handleAnalyze = () => {
    if (!selectedFile) return;
    simulateProcessing();
  };

  const handleReAnalyze = () => {
    setAnalysisData(null);
    setStatus('idle');
    setSelectedFile(null);
  };

  const handleFrameClick = (frameIndex) => {
    if (expandedFrame === frameIndex) {
      setExpandedFrame(null);
    } else {
      setExpandedFrame(frameIndex);
      setFrameStageTab(0);
    }
  };

  // Derived
  const maxTiltFrame = analysisData?.frames?.reduce((max, f) =>
    Math.abs(f.tilt_angle) > Math.abs(max.tilt_angle) ? f : max
  , analysisData.frames[0]);

  const band = analysisData ? getTiltBand(analysisData.max_tilt) : null;
  const BandIcon = band?.icon;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3, maxHeight: '90vh' }
      }}
    >
      {/* Header */}
      <DialogTitle sx={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        py: 1.5, px: 3
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <StraightenIcon sx={{ color: DEEP_PURPLE, transform: 'rotate(90deg)' }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
              Slanting Analysis
            </Typography>
            {asset && (
              <Typography variant="caption" color="text.secondary">
                {asset.name || asset.code} {asset.code ? `(${asset.code})` : ''}
              </Typography>
            )}
          </Box>
          {status === 'completed' && (
            <Chip
              label="Completed"
              size="small"
              icon={<CheckCircleIcon sx={{ fontSize: '0.9rem' }} />}
              sx={{ ml: 1, bgcolor: alpha('#4caf50', 0.1), color: '#4caf50', fontWeight: 600 }}
            />
          )}
          {status === 'processing' && (
            <Chip
              label="Processing"
              size="small"
              sx={{ ml: 1, bgcolor: alpha(DEEP_PURPLE, 0.1), color: DEEP_PURPLE, fontWeight: 600 }}
            />
          )}
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pt: 3, pb: 2 }}>
        {/* Loading */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress sx={{ color: DEEP_PURPLE }} />
          </Box>
        )}

        {!loading && (
          <>
            {/* Assessment Summary — shown when completed */}
            {status === 'completed' && analysisData && band && (
              <Paper
                elevation={0}
                sx={{
                  p: 2.5, mt: 3, mb: 3, borderRadius: 2,
                  borderLeft: `4px solid ${band.color}`,
                  bgcolor: alpha(band.color, 0.04),
                  border: `1px solid ${alpha(band.color, 0.15)}`,
                  borderLeftWidth: 4
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  {BandIcon && <BandIcon sx={{ color: band.color, fontSize: '1.2rem' }} />}
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Assessment Summary
                  </Typography>
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: '1fr 1fr 1fr 1fr' }, gap: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Max Tilt</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: band.color }}>
                      {analysisData.max_tilt.toFixed(1)}°
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Direction</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <NavigationIcon sx={{
                        fontSize: '1.2rem', color: DEEP_PURPLE,
                        transform: `rotate(${analysisData.tilt_direction_degrees}deg)`
                      }} />
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {analysisData.tilt_direction || getDirectionLabel(analysisData.tilt_direction_degrees)}
                      </Typography>
                    </Box>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Classification</Typography>
                    <Chip
                      label={band.label}
                      size="small"
                      sx={{ mt: 0.5, bgcolor: alpha(band.color, 0.15), color: band.color, fontWeight: 600, fontSize: '0.7rem' }}
                    />
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Analysis Date</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500, mt: 0.5 }}>
                      {new Date(analysisData.created_at).toLocaleDateString()}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            )}

            {/* Upload Section — shown when idle */}
            {(status === 'idle' || status === 'completed') && (
              <Paper
                elevation={0}
                sx={{
                  p: 3, mb: 3, borderRadius: 2,
                  border: `2px dashed ${dragOver ? DEEP_PURPLE : alpha(theme.palette.divider, 0.3)}`,
                  bgcolor: dragOver ? alpha(DEEP_PURPLE, 0.04) : 'transparent',
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="video/mp4,video/quicktime,video/x-msvideo,.mp4,.mov,.avi"
                  style={{ display: 'none' }}
                  onChange={handleFileSelect}
                />
                {!selectedFile ? (
                  <>
                    <CloudUploadIcon sx={{ fontSize: 40, color: alpha(DEEP_PURPLE, 0.5), mb: 1 }} />
                    <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
                      {status === 'completed' ? 'Upload New Orbit Video (Re-analyze)' : 'Upload Orbit Video'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Drag & drop or click to select — MP4, MOV, AVI
                    </Typography>
                  </>
                ) : (
                  <Box onClick={(e) => e.stopPropagation()}>
                    <VideoFileIcon sx={{ fontSize: 40, color: DEEP_PURPLE, mb: 1 }} />
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                      {selectedFile.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {(selectedFile.size / (1024 * 1024)).toFixed(1)} MB
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                      <Button
                        variant="contained"
                        onClick={handleAnalyze}
                        sx={{
                          bgcolor: DEEP_PURPLE, borderRadius: 2, textTransform: 'none', fontWeight: 600,
                          '&:hover': { bgcolor: alpha(DEEP_PURPLE, 0.85) }
                        }}
                      >
                        Analyze Slanting
                      </Button>
                    </Box>
                  </Box>
                )}
              </Paper>
            )}

            {/* Processing Status */}
            {status === 'processing' && (
              <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 2, border: `1px solid ${alpha(DEEP_PURPLE, 0.15)}` }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                  <CircularProgress size={20} sx={{ color: DEEP_PURPLE }} />
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {PROCESSING_STAGES[processingStage]}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={processingProgress}
                  sx={{
                    height: 8, borderRadius: 4,
                    bgcolor: alpha(DEEP_PURPLE, 0.1),
                    '& .MuiLinearProgress-bar': { bgcolor: DEEP_PURPLE, borderRadius: 4 }
                  }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', textAlign: 'right' }}>
                  Stage {processingStage + 1} of {PROCESSING_STAGES.length}
                </Typography>
              </Paper>
            )}

            {/* Frame Gallery */}
            {status === 'completed' && analysisData?.frames && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                  Frame Analysis ({analysisData.frames.length} Frames)
                </Typography>
                <Box sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(5, 1fr)' },
                  gap: 1.5
                }}>
                  {analysisData.frames.map((frame) => {
                    const frameBand = getTiltBand(frame.tilt_angle);
                    const isMax = maxTiltFrame && frame.frame_index === maxTiltFrame.frame_index;
                    const isExpanded = expandedFrame === frame.frame_index;
                    return (
                      <Paper
                        key={frame.frame_index}
                        elevation={0}
                        onClick={() => handleFrameClick(frame.frame_index)}
                        sx={{
                          p: 1.5, borderRadius: 2, cursor: 'pointer',
                          border: `2px solid ${isMax ? frameBand.color : isExpanded ? DEEP_PURPLE : alpha(theme.palette.divider, 0.15)}`,
                          bgcolor: isExpanded ? alpha(DEEP_PURPLE, 0.04) : 'transparent',
                          transition: 'all 0.2s',
                          '&:hover': { bgcolor: alpha(DEEP_PURPLE, 0.04), borderColor: alpha(DEEP_PURPLE, 0.3) }
                        }}
                      >
                        {/* Placeholder image */}
                        <Box sx={{
                          height: 80, borderRadius: 1, mb: 1,
                          bgcolor: alpha(theme.palette.grey[500], 0.08),
                          display: 'flex', flexDirection: 'column',
                          alignItems: 'center', justifyContent: 'center'
                        }}>
                          <CameraAltIcon sx={{ fontSize: 24, color: 'text.disabled', mb: 0.5 }} />
                          <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.disabled' }}>
                            {frame.viewing_angle}°
                          </Typography>
                        </Box>
                        {/* Tilt value */}
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 700, textAlign: 'center', color: frameBand.color }}
                        >
                          {frame.tilt_angle.toFixed(1)}°
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', color: 'text.secondary', fontSize: '0.65rem' }}>
                          View: {frame.viewing_angle}°
                        </Typography>
                        {isMax && (
                          <Chip
                            label="MAX"
                            size="small"
                            sx={{
                              mt: 0.5, width: '100%', height: 18,
                              bgcolor: alpha(frameBand.color, 0.15), color: frameBand.color,
                              fontWeight: 700, fontSize: '0.6rem'
                            }}
                          />
                        )}
                      </Paper>
                    );
                  })}
                </Box>

                {/* Expanded Frame Detail */}
                <Collapse in={expandedFrame !== null} timeout={300}>
                  {expandedFrame !== null && analysisData.frames[expandedFrame] && (
                    <Paper elevation={0} sx={{ mt: 2, p: 2, borderRadius: 2, border: `1px solid ${alpha(DEEP_PURPLE, 0.15)}` }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                        Frame {expandedFrame + 1} — Viewing Angle: {analysisData.frames[expandedFrame].viewing_angle}°
                      </Typography>
                      <Tabs
                        value={frameStageTab}
                        onChange={(_, v) => setFrameStageTab(v)}
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{ mb: 2, minHeight: 36, '& .MuiTab-root': { minHeight: 36, py: 0.5, fontSize: '0.75rem', textTransform: 'none' } }}
                      >
                        {FRAME_STAGES.map((stage, i) => (
                          <Tab key={stage.key} label={stage.label} />
                        ))}
                      </Tabs>
                      {/* Stage placeholder */}
                      <Box sx={{
                        height: 200, borderRadius: 2,
                        bgcolor: alpha(theme.palette.grey[500], 0.06),
                        border: `1px dashed ${alpha(theme.palette.divider, 0.2)}`,
                        display: 'flex', flexDirection: 'column',
                        alignItems: 'center', justifyContent: 'center'
                      }}>
                        <CameraAltIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                          {FRAME_STAGES[frameStageTab]?.label}
                        </Typography>
                        <Typography variant="caption" color="text.disabled">
                          Frame {expandedFrame + 1} — Processing output will appear here
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 3, mt: 1.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          Tilt: <strong>{analysisData.frames[expandedFrame].tilt_angle.toFixed(1)}°</strong>
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Gimbal Pitch: <strong>{analysisData.frames[expandedFrame].gimbal_pitch.toFixed(1)}°</strong>
                        </Typography>
                      </Box>
                    </Paper>
                  )}
                </Collapse>
              </Box>
            )}

            {/* Measurements Table */}
            {status === 'completed' && analysisData?.frames && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                  Tilt Measurements
                </Typography>
                <TableContainer sx={{ borderRadius: 2, border: `1px solid ${alpha(theme.palette.divider, 0.08)}` }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: alpha(DEEP_PURPLE, 0.04) }}>
                        <TableCell sx={{ fontWeight: 600, py: 1.2 }}>Frame</TableCell>
                        <TableCell sx={{ fontWeight: 600, py: 1.2 }}>Viewing Angle</TableCell>
                        <TableCell sx={{ fontWeight: 600, py: 1.2 }}>Tilt Angle</TableCell>
                        <TableCell sx={{ fontWeight: 600, py: 1.2 }}>Gimbal Pitch</TableCell>
                        <TableCell sx={{ fontWeight: 600, py: 1.2 }}>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {analysisData.frames.map((frame) => {
                        const frameBand = getTiltBand(frame.tilt_angle);
                        const isMax = maxTiltFrame && frame.frame_index === maxTiltFrame.frame_index;
                        return (
                          <TableRow
                            key={frame.frame_index}
                            sx={{
                              bgcolor: isMax ? alpha(frameBand.color, 0.06) : 'transparent',
                              '& .MuiTableCell-root': { py: 1, borderColor: alpha(theme.palette.divider, 0.06) }
                            }}
                          >
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: isMax ? 700 : 400 }}>
                                #{frame.frame_index + 1}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">{frame.viewing_angle}°</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: frameBand.color }}>
                                {frame.tilt_angle.toFixed(1)}°
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">{frame.gimbal_pitch.toFixed(1)}°</Typography>
                            </TableCell>
                            <TableCell>
                              {isMax ? (
                                <Chip label="MAX TILT" size="small" sx={{ height: 20, bgcolor: alpha(frameBand.color, 0.15), color: frameBand.color, fontWeight: 700, fontSize: '0.6rem' }} />
                              ) : (
                                <Chip label={frameBand.label.split(' ')[0]} size="small" sx={{ height: 20, bgcolor: alpha(frameBand.color, 0.1), color: frameBand.color, fontSize: '0.6rem' }} />
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {/* Methodology Reference */}
            <Accordion
              elevation={0}
              sx={{
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                borderRadius: '12px !important',
                '&:before': { display: 'none' },
                '&.Mui-expanded': { margin: 0 }
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <MenuBookIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Methodology Reference
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                {/* Interpretation thresholds */}
                <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
                  Tilt Interpretation Thresholds
                </Typography>
                <TableContainer sx={{ mb: 2, borderRadius: 1, border: `1px solid ${alpha(theme.palette.divider, 0.08)}` }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: alpha(theme.palette.grey[500], 0.04) }}>
                        <TableCell sx={{ fontWeight: 600, py: 0.8, fontSize: '0.75rem' }}>Tilt Range</TableCell>
                        <TableCell sx={{ fontWeight: 600, py: 0.8, fontSize: '0.75rem' }}>Interpretation</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {TILT_BANDS.map((b) => (
                        <TableRow key={b.label}>
                          <TableCell sx={{ py: 0.6 }}>
                            <Typography variant="caption">
                              {b.max === Infinity ? `> ${b.min}°` : `${b.min}° — ${b.max}°`}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 0.6 }}>
                            <Chip
                              label={b.label}
                              size="small"
                              sx={{ height: 20, bgcolor: alpha(b.color, 0.12), color: b.color, fontWeight: 600, fontSize: '0.65rem' }}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Multi-angle reading guide */}
                <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
                  How to Read Multi-Angle Results
                </Typography>
                <Box sx={{ pl: 1, mb: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    1. The <strong>largest tilt value</strong> across all frames is the best estimate of the pole's actual lean.
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    2. The <strong>viewing angle that produced the max reading</strong> indicates the lean direction (the pole leans perpendicular to that view).
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    3. Frames showing <strong>near-zero tilt</strong> mean the pole is leaning directly toward/away from those positions.
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    4. <strong>Do not average all 10 values</strong> — the maximum is the most meaningful single number.
                  </Typography>
                </Box>

                {/* Field procedure */}
                <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
                  Field Capture Requirements
                </Typography>
                <Box sx={{ pl: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    - Gimbal pitch must be <strong>0° (horizontal)</strong>
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    - Shaft should <strong>fill the frame vertically</strong> (close-up preferred)
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    - Single <strong>slow 360° orbit</strong> around the mast
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    - Capture during <strong>calm weather</strong> (no wind sway)
                  </Typography>
                </Box>
              </AccordionDetails>
            </Accordion>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5, borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
        {status === 'completed' && (
          <Button
            onClick={handleReAnalyze}
            sx={{ textTransform: 'none', color: 'text.secondary' }}
          >
            Re-analyze
          </Button>
        )}
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{ textTransform: 'none', borderRadius: 2 }}
        >
          Close
        </Button>
        {status === 'completed' && (
          <Tooltip title="Export functionality coming soon">
            <span>
              <Button
                variant="contained"
                disabled
                sx={{
                  textTransform: 'none', borderRadius: 2,
                  bgcolor: DEEP_PURPLE, '&:hover': { bgcolor: alpha(DEEP_PURPLE, 0.85) }
                }}
              >
                Export Report
              </Button>
            </span>
          </Tooltip>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SlantingAnalysisModal;
